/**
 * Contact Us template — renderer + editor + default data.
 * Used in PagesAdmin (editor) and StorefrontPage (renderer).
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

export const CONTACT_US_DEFAULT = {
  hero: {
    eyebrow: "We're here to help",
    headline: "Contact Us",
    hl_accent: "Us",
    subheadline: "We'd Love to Hear From You!",
    lead: "At Importrend, your questions, suggestions and feedback matter to us. Whether you need order assistance or product information, our team is happy to help.",
    btn_email_text: "✉ Email Support ↗",
    btn_email_href: "mailto:support@importrend.com",
    btn_phone_text: "☎ Call Us",
    btn_phone_href: "tel:+917011608563",
  },
  contact_cards: [
    { icon: "🕘", title: "Office Timings", line1: "09:00 to 18:00 IST", line2: "Monday to Saturday", note: "Closed on Sundays", variant: "peach" },
    { icon: "✉️", title: "Email Us", body: "For order questions, feedback and support.", link_text: "support@importrend.com ↗", link_href: "mailto:support@importrend.com", variant: "blue" },
    { icon: "📞", title: "Call Us", body: "Speak with our team during office hours.", link_text: "+91 7011608563 ↗", link_href: "tel:+917011608563", variant: "green" },
  ],
  support_cards: [
    { icon: "💬", title: "Quick Response", text: "We aim to respond to queries as soon as possible during business hours.", variant: "pink" },
    { icon: "🎧", title: "Friendly Support", text: "Our team is ready to listen and guide you through your questions.", variant: "blue" },
    { icon: "📦", title: "Order Assistance", text: "Get help with order updates, tracking, delivery and payment questions.", variant: "green" },
    { icon: "💡", title: "Product Information", text: "Need product details or help choosing an item? We're here to assist.", variant: "lavender" },
  ],
  form_section: {
    eyebrow: "Send us a message",
    headline: "Still Have Questions?",
    hl_accent: "Questions?",
    body: "Tell us how we can help. For order-related enquiries, including your order ID helps our team understand your request.",
    steps: [
      { title: "Share your question", desc: "Include your name, email and order ID if relevant." },
      { title: "Our team reviews it", desc: "We check the details and work on a response." },
      { title: "We get back to you", desc: "We reply using the contact details you provide." },
    ],
    form_title: "Write to our team",
    form_body: "This form opens your email app with your message prepared.",
    email_to: "support@importrend.com",
  },
};

const CU_CSS = `
.cu-root{--ink:#221815;--orange:#db5a14;--muted:#6b5c55;--cream:#fff8f2;--line:#f0e2d7;color:var(--ink);background:#fff;font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Arial,sans-serif;line-height:1.55}
.cu-root *{box-sizing:border-box}
.cu-root h1,.cu-root h2,.cu-root h3,.cu-root p{margin-top:0}
.cu-root a{color:var(--orange)}
.cu-wrap{width:min(1200px,calc(100% - 44px));margin:auto}
.cu-orange{color:var(--orange)}
.cu-eyebrow{font-size:.77rem;letter-spacing:.18em;text-transform:uppercase;font-weight:800;color:var(--orange);margin-bottom:12px}
.cu-hero{background:radial-gradient(circle at 90% 10%,#ffd9be 0,transparent 38%),linear-gradient(125deg,#fffaf5,#fff1e4);padding:72px 0 60px;overflow:hidden}
.cu-hero-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:48px;align-items:center}
.cu-hero h1{font-size:clamp(2.4rem,4.2vw,4.4rem);line-height:1.05;letter-spacing:-.04em;margin-bottom:14px}
.cu-hero h2{font-size:clamp(1.1rem,1.8vw,1.4rem);font-weight:600;margin-bottom:16px;color:var(--muted)}
.cu-lead{font-size:clamp(.95rem,1.3vw,1.1rem);color:var(--muted);max-width:560px;margin-bottom:24px}
.cu-hero-actions{display:flex;gap:12px;flex-wrap:wrap}
.cu-btn{display:inline-block;text-decoration:none;padding:12px 22px;border-radius:999px;font-weight:800;font-size:.9rem;background:var(--orange);color:#fff;transition:opacity .15s}
.cu-btn:hover{opacity:.88}
.cu-btn-sec{background:transparent;border:2px solid var(--orange);color:var(--orange)}
.cu-visual{position:relative;min-height:300px;border-radius:28px;background:linear-gradient(135deg,#ffe2c4,#ffd5a8);display:grid;place-items:center;box-shadow:0 20px 60px #c57a3022}
.cu-agent{font-size:clamp(80px,12vw,130px);filter:drop-shadow(0 12px 10px #a8562a28);z-index:1}
.cu-bubble{position:absolute;background:#fff;border-radius:16px;padding:10px 15px;font-size:.88rem;font-weight:700;box-shadow:0 10px 28px #8f4e2418}
.cu-b1{top:22px;left:18px}.cu-b2{top:22px;right:18px}.cu-b3{bottom:26px;right:18px}
.cu-caption{position:absolute;bottom:14px;left:18px;font-size:.78rem;font-weight:700;color:#9b6644;background:#fff9f4;padding:5px 12px;border-radius:20px}
.cu-section{padding:60px 0}
.cu-section.cu-soft{background:var(--cream)}
.cu-sh{text-align:center;max-width:680px;margin:0 auto 32px}
.cu-sh h2{font-size:clamp(1.6rem,2.5vw,2.6rem);letter-spacing:-.03em;margin-bottom:12px}
.cu-grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}
.cu-grid4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:18px}
.cu-card{border-radius:22px;background:#fff;padding:26px;border:1px solid var(--line);box-shadow:0 8px 26px #6e3a0c08}
.cu-card p:last-child{margin-bottom:0}
.cu-icon{width:52px;height:52px;border-radius:16px;display:grid;place-items:center;font-size:1.55rem;margin-bottom:14px}
.cu-peach{background:#ffe7d4}.cu-blue{background:#dff1ff}.cu-green{background:#ddf6e8}.cu-pink{background:#ffdde5}.cu-lavender{background:#ede8ff}
.cu-two-col{display:grid;grid-template-columns:1.1fr .9fr;gap:40px;align-items:start}
.cu-panel{}
.cu-steps{display:flex;flex-direction:column;gap:14px;margin-top:20px}
.cu-step{display:flex;gap:14px;align-items:flex-start}
.cu-step-num{width:34px;height:34px;border-radius:50%;background:var(--orange);color:#fff;font-weight:900;display:grid;place-items:center;flex-shrink:0;font-size:.9rem}
.cu-step small{display:block;font-size:.85rem;color:var(--muted);margin-top:3px}
.cu-form{background:#fff;border:1px solid var(--line);border-radius:22px;padding:28px;box-shadow:0 8px 26px #6e3a0c08}
.cu-form h3{font-size:1.5rem;margin-bottom:8px}
.cu-form label{display:block;font-size:.82rem;font-weight:700;color:var(--muted);margin-bottom:14px}
.cu-form label input,.cu-form label select,.cu-form label textarea{display:block;width:100%;margin-top:5px;padding:10px 14px;border:1px solid var(--line);border-radius:10px;font-size:.93rem;outline:none;font-family:inherit}
.cu-form label textarea{min-height:90px;resize:vertical}
.cu-form-btn{width:100%;padding:13px;border:none;border-radius:999px;background:var(--orange);color:#fff;font-weight:800;font-size:1rem;cursor:pointer;margin-top:4px}
.cu-form-note{font-size:.77rem;color:var(--muted);margin-top:10px;margin-bottom:0}
@media(max-width:900px){.cu-hero-grid,.cu-two-col{grid-template-columns:1fr}.cu-grid4{grid-template-columns:repeat(2,1fr)}.cu-grid3{grid-template-columns:1fr 1fr}}
@media(max-width:600px){.cu-wrap{width:calc(100% - 28px)}.cu-hero{padding:42px 0}.cu-grid3,.cu-grid4{grid-template-columns:1fr}.cu-visual{min-height:230px}.cu-section{padding:44px 0}}
`;

function accentify(text, accent) {
  if (!accent || !text.includes(accent)) return <span>{text}</span>;
  const parts = text.split(accent);
  return <span>{parts[0]}<span className="cu-orange">{accent}</span>{parts.slice(1).join(accent)}</span>;
}

const cardBg = (v) => ({ peach: "cu-peach", blue: "cu-blue", green: "cu-green", pink: "cu-pink", lavender: "cu-lavender" }[v] || "cu-peach");

export function ContactUsRenderer({ data }) {
  const d = data || CONTACT_US_DEFAULT;
  const h  = d.hero          || CONTACT_US_DEFAULT.hero;
  const cc = d.contact_cards  || CONTACT_US_DEFAULT.contact_cards;
  const sc = d.support_cards  || CONTACT_US_DEFAULT.support_cards;
  const fs = d.form_section   || CONTACT_US_DEFAULT.form_section;
  const steps = fs.steps      || CONTACT_US_DEFAULT.form_section.steps;

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.name?.value || "";
    const email = form.email?.value || "";
    const topic = form.topic?.value || "";
    const order = form.order?.value || "";
    const message = form.message?.value || "";
    const body = `Name: ${name}\nEmail: ${email}\nTopic: ${topic}\nOrder ID: ${order}\n\n${message}`;
    window.location.href = `mailto:${fs.email_to}?subject=Support Request - ${topic}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="cu-root">
      <style>{CU_CSS}</style>

      {/* Hero */}
      <section className="cu-hero">
        <div className="cu-wrap cu-hero-grid">
          <div>
            <div className="cu-eyebrow">{h.eyebrow}</div>
            <h1>{accentify(h.headline, h.hl_accent)}</h1>
            <h2>{h.subheadline}</h2>
            <p className="cu-lead">{h.lead}</p>
            <div className="cu-hero-actions">
              <a className="cu-btn" href={h.btn_email_href}>{h.btn_email_text}</a>
              <a className="cu-btn cu-btn-sec" href={h.btn_phone_href}>{h.btn_phone_text}</a>
            </div>
          </div>
          <div className="cu-visual">
            <div className="cu-agent">👩🏻‍💻</div>
            <div className="cu-bubble cu-b1">💬</div>
            <div className="cu-bubble cu-b2">✉️</div>
            <div className="cu-bubble cu-b3">📞</div>
            <div className="cu-caption">IMPORTREND · Support Team</div>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="cu-section">
        <div className="cu-wrap">
          <div className="cu-sh">
            <div className="cu-eyebrow">Get in touch</div>
            <h2>Ways to <span className="cu-orange">Reach Us</span></h2>
            <p style={{color:"#6b5c55"}}>Choose the most convenient way to contact our team during business hours.</p>
          </div>
          <div className="cu-grid3">
            {cc.map((card, i) => (
              <article key={i} className="cu-card">
                <div className={`cu-icon ${cardBg(card.variant)}`}>{card.icon}</div>
                <h3>{card.title}</h3>
                {card.line1 && <p><strong>{card.line1}</strong><br />{card.line2}</p>}
                {card.note && <p style={{fontSize:"13px"}}>{card.note}</p>}
                {card.body && <p>{card.body}</p>}
                {card.link_text && <p style={{marginTop:"10px"}}><a href={card.link_href}>{card.link_text}</a></p>}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Support Cards */}
      <section className="cu-section cu-soft">
        <div className="cu-wrap">
          <div className="cu-sh">
            <div className="cu-eyebrow">How we can help</div>
            <h2>Support for <span className="cu-orange">Every Step</span></h2>
            <p style={{color:"#6b5c55"}}>From product questions to post-purchase assistance, reach out whenever you need us.</p>
          </div>
          <div className="cu-grid4">
            {sc.map((card, i) => (
              <article key={i} className="cu-card">
                <div className={`cu-icon ${cardBg(card.variant)}`}>{card.icon}</div>
                <h3>{card.title}</h3>
                <p style={{color:"#6b5c55",fontSize:".93rem"}}>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="cu-section">
        <div className="cu-wrap cu-two-col">
          <div className="cu-panel">
            <div className="cu-eyebrow">{fs.eyebrow}</div>
            <h2>{accentify(fs.headline, fs.hl_accent)}</h2>
            <p style={{color:"#6b5c55"}}>{fs.body}</p>
            <div className="cu-steps">
              {steps.map((s, i) => (
                <div key={i} className="cu-step">
                  <div className="cu-step-num">{i + 1}</div>
                  <div>
                    <strong>{s.title}</strong>
                    <small>{s.desc}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="cu-form">
            <h3>{fs.form_title}</h3>
            <p style={{color:"#6b5c55",marginBottom:"20px"}}>{fs.form_body}</p>
            <form onSubmit={handleSubmit}>
              <label>Your Name<input name="name" required placeholder="Enter your full name" /></label>
              <label>Email Address<input name="email" type="email" required placeholder="you@example.com" /></label>
              <label>How can we help?
                <select name="topic">
                  <option>General Enquiry</option>
                  <option>Order Support</option>
                  <option>Product Information</option>
                  <option>Feedback</option>
                  <option>Other</option>
                </select>
              </label>
              <label>Order ID (optional)<input name="order" placeholder="Enter your order ID" /></label>
              <label>Your Message<textarea name="message" required placeholder="Tell us more about your question..." /></label>
              <button className="cu-form-btn" type="submit">✉ Prepare Email →</button>
              <p className="cu-form-note">No information is stored on this page. Your default email app will open so you can review and send the message.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Editor helpers ─────────────────────────────────────────────────────────
function Field({ label, value, onChange, multiline = false, hint }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={3} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

export function ContactUsEditor({ data, onChange }) {
  const d = data || CONTACT_US_DEFAULT;
  const set = (section, field, value) => onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  const setCard = (section, idx, field, value) => {
    const arr = [...(d[section] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    onChange({ ...d, [section]: arr });
  };
  const setStep = (idx, field, value) => {
    const steps = [...((d.form_section?.steps) || [])];
    steps[idx] = { ...steps[idx], [field]: value };
    onChange({ ...d, form_section: { ...(d.form_section || {}), steps } });
  };

  const h  = d.hero          || {};
  const cc = d.contact_cards  || [];
  const sc = d.support_cards  || [];
  const fs = d.form_section   || {};
  const steps = fs.steps || [];

  const VARIANTS = ["peach", "blue", "green", "pink", "lavender"];

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>Contact Us Template</strong> — Edit each section below.
      </div>

      <Section title="1. Hero Section" defaultOpen>
        <Field label="Eyebrow" value={h.eyebrow} onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline" value={h.headline} onChange={v => set("hero","headline",v)} />
        <Field label="Accent word(s)" value={h.hl_accent} onChange={v => set("hero","hl_accent",v)} hint="Must be exact substring of Headline" />
        <Field label="Subheadline" value={h.subheadline} onChange={v => set("hero","subheadline",v)} />
        <Field label="Lead paragraph" value={h.lead} onChange={v => set("hero","lead",v)} multiline />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email button text" value={h.btn_email_text} onChange={v => set("hero","btn_email_text",v)} />
          <Field label="Email button link" value={h.btn_email_href} onChange={v => set("hero","btn_email_href",v)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone button text" value={h.btn_phone_text} onChange={v => set("hero","btn_phone_text",v)} />
          <Field label="Phone button link" value={h.btn_phone_href} onChange={v => set("hero","btn_phone_href",v)} />
        </div>
      </Section>

      <Section title="2. Contact Cards (3 cards)">
        {cc.map((card, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Card {i+1}</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Icon" value={card.icon} onChange={v => setCard("contact_cards",i,"icon",v)} />
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Color</label>
                <select value={card.variant || "peach"} onChange={e => setCard("contact_cards",i,"variant",e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                  {VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <Field label="Title" value={card.title} onChange={v => setCard("contact_cards",i,"title",v)} />
            <Field label="Line 1 (bold)" value={card.line1} onChange={v => setCard("contact_cards",i,"line1",v)} />
            <Field label="Line 2" value={card.line2} onChange={v => setCard("contact_cards",i,"line2",v)} />
            <Field label="Note (small)" value={card.note} onChange={v => setCard("contact_cards",i,"note",v)} />
            <Field label="Body text" value={card.body} onChange={v => setCard("contact_cards",i,"body",v)} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Link text" value={card.link_text} onChange={v => setCard("contact_cards",i,"link_text",v)} />
              <Field label="Link href" value={card.link_href} onChange={v => setCard("contact_cards",i,"link_href",v)} />
            </div>
          </div>
        ))}
      </Section>

      <Section title="3. Support Cards (4 cards)">
        {sc.map((card, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Icon" value={card.icon} onChange={v => setCard("support_cards",i,"icon",v)} />
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Color</label>
                <select value={card.variant || "pink"} onChange={e => setCard("support_cards",i,"variant",e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                  {VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <Field label="Title" value={card.title} onChange={v => setCard("support_cards",i,"title",v)} />
            <Field label="Text" value={card.text} onChange={v => setCard("support_cards",i,"text",v)} />
          </div>
        ))}
      </Section>

      <Section title="4. Form Section">
        <Field label="Eyebrow" value={fs.eyebrow} onChange={v => set("form_section","eyebrow",v)} />
        <Field label="Headline" value={fs.headline} onChange={v => set("form_section","headline",v)} />
        <Field label="Accent word(s)" value={fs.hl_accent} onChange={v => set("form_section","hl_accent",v)} />
        <Field label="Body text" value={fs.body} onChange={v => set("form_section","body",v)} multiline />
        <Field label="Form card title" value={fs.form_title} onChange={v => set("form_section","form_title",v)} />
        <Field label="Form card body" value={fs.form_body} onChange={v => set("form_section","form_body",v)} />
        <Field label="Send email to" value={fs.email_to} onChange={v => set("form_section","email_to",v)} />
        <p className="text-[11px] font-bold text-slate-500 uppercase mt-2">Process Steps</p>
        {steps.map((s, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <Field label={`Step ${i+1} Title`} value={s.title} onChange={v => setStep(i,"title",v)} />
            <Field label="Description" value={s.desc} onChange={v => setStep(i,"desc",v)} />
          </div>
        ))}
      </Section>
    </div>
  );
}
